/* eslint-disable @typescript-eslint/no-explicit-any */
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useAccountStorage } from '@juvare/contact-management/AppClients';
import { allowAccessToAccounts, getAccountsViewFilter } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import HelpIcon from '@mui/icons-material/Help';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Tooltip, } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useDocumentLibraryTranslations } from '../../../hooks';
import { fetchAccordionData, getPermissionsUpdated, mergePermissions, transformToDocumentPermissions } from '../utils';
import { PermissionsAccordions } from './PermissionsAccordions';
import { PermissionsSelection } from './PermissionsSelection';
export const PermissionsDialog = (props) => {
    const userState = useUserState();
    const { t } = useDocumentLibraryTranslations();
    const accountStorage = useAccountStorage();
    const accountsFilter = React.useMemo(() => (userState ? getAccountsViewFilter(userState, true) : []), [userState]);
    const canAccessAccounts = React.useMemo(() => (userState ? allowAccessToAccounts(userState) : false), [userState]);
    const { systemRoleMap } = useSelector(getSettings);
    const [hiddenPermissions, setHiddenPermissions] = React.useState({});
    const [publicRead, setPublicRead] = React.useState(props.permissions.public || false);
    const [accounts, setAccounts] = React.useState([]);
    const [organizations, setOrganizations] = React.useState([]);
    const [systemRoles, setSystemRoles] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [type, setType] = React.useState('read');
    const [addAccountsModalOpen, setAddAccountsModalOpen] = React.useState(false);
    const [selectedAccountsIds, setSelectedAccountsIds] = React.useState([]);
    const [selectedOrganizationsIds, setSelectedOrganizationsIds] = React.useState([]);
    const [selectedSystemRoles, setSelectedSystemRoles] = React.useState([]);
    const handleSelectedAccountsAndOrganizationsIds = (props) => {
        setSelectedAccountsIds(props.accountIds);
        setSelectedOrganizationsIds(props.organizationIds);
        setSelectedSystemRoles(props.systemRoles);
    };
    const handleRemoveSelectedOrganizationsAndAccounts = () => {
        setAccounts(accounts.filter((item) => !selectedAccountsIds.includes(item.id)));
        setOrganizations(organizations.filter((item) => !selectedOrganizationsIds.includes(item.id)));
        setSystemRoles(systemRoles.filter((item) => !selectedSystemRoles.includes(item.name)));
        setSelectedAccountsIds([]);
        setSelectedOrganizationsIds([]);
        setSelectedSystemRoles([]);
    };
    const fetchData = React.useCallback((documentPermissions, initialFetch = false) => fetchAccordionData({
        permissions: documentPermissions,
        accountStorage: accountStorage,
        userOrganizations: (userState === null || userState === void 0 ? void 0 : userState.organizations) || [],
        accountsFilter,
        canAccessAccounts,
        initialFetch,
        systemRoleMap,
    })
        .then((items) => {
        setAccounts(items.accounts);
        setOrganizations(items.organizations);
        setSystemRoles(items.systemRoles);
        if (initialFetch) {
            setHiddenPermissions(items.hiddenPermissions);
        }
    })
        .catch(handleErrorSnackbar), [accountStorage, accountsFilter, canAccessAccounts, userState === null || userState === void 0 ? void 0 : userState.organizations, systemRoleMap]);
    React.useEffect(() => {
        setLoading(true);
        fetchData({
            read: props.permissions.read,
            write: props.permissions.write,
            modify: props.permissions.modify,
        }, true).finally(() => setLoading(false));
    }, [props.permissions, fetchData]);
    const handleSinglePermissionChange = ({ key, id, type }) => {
        const accountIds = (key === 'accountIds' && [id]) || [];
        const organizationIds = (key === 'organizationIds' && [id]) || [];
        const systemRoles = (key === 'systemRoles' && [id]) || [];
        setAccounts((prevState) => prevState.map((acc) => (accountIds.includes(acc.id) ? Object.assign(Object.assign({}, acc), { permission: type }) : acc)));
        setOrganizations((prevState) => prevState.map((org) => (organizationIds.includes(org.id) ? Object.assign(Object.assign({}, org), { permission: type }) : org)));
        setSystemRoles((prevState) => prevState.map((role) => (systemRoles.includes(role.name) ? Object.assign(Object.assign({}, role), { permission: type }) : role)));
    };
    const handleSelectionSave = (accountIds = [], organizationIds = [], selectedSystemRoles = []) => {
        const documentPermissions = transformToDocumentPermissions(accounts, organizations, systemRoles);
        fetchData(getPermissionsUpdated(documentPermissions, accountIds, organizationIds, selectedSystemRoles, type)).finally(() => setAddAccountsModalOpen(false));
    };
    const handleSave = () => {
        const transformed = transformToDocumentPermissions(accounts, organizations, systemRoles);
        const permissions = mergePermissions(hiddenPermissions, transformed);
        props.onSubmit(Object.assign(Object.assign({}, permissions), { public: publicRead }));
    };
    if (loading) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, null, t('documents.dialogTitle')),
        React.createElement(DialogContent, null,
            React.createElement(Box, { display: 'flex', alignItems: 'center' },
                React.createElement(FormControl, null,
                    React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: publicRead, onChange: (event) => setPublicRead(event.target.checked) }), label: t('documents.publicAccess'), "data-test": 'public-access-checkbox' })),
                React.createElement(Tooltip, { title: t('documents.anyoneCanRead') },
                    React.createElement(IconButton, { size: 'small' },
                        React.createElement(HelpIcon, { fontSize: 'small' })))),
            React.createElement(Box, { sx: { mt: 2, mb: 2 } },
                React.createElement(FormControl, { sx: { mr: 2, mb: 2 } },
                    React.createElement(InputLabel, { id: 'permission-type-select-label' }, t('common.type')),
                    React.createElement(Select, { labelId: 'permission-type-select-label', id: 'permission-type-select', "data-test": 'permission-type-select', value: type, fullWidth: true, variant: 'outlined', size: 'small', label: t('common.type'), onChange: (event) => setType(event.target.value) },
                        React.createElement(MenuItem, { "data-test": 'read-menu-item', value: 'read' }, t('documents.read')),
                        React.createElement(MenuItem, { "data-test": 'write-menu-item', value: 'write' }, t('documents.write')),
                        React.createElement(MenuItem, { "data-test": 'modify-menu-item', value: 'modify' }, t('documents.modify')))),
                React.createElement(Button, { sx: { mr: 2, mb: 2 }, variant: 'outlined', color: 'primary', onClick: () => setAddAccountsModalOpen(true), "data-test": 'add-permissions-button' }, t('common.add')),
                React.createElement(Button, { sx: { mr: 2, mb: 2 }, variant: 'outlined', color: 'primary', "data-test": 'clear-all-permission-button', onClick: () => {
                        setAccounts([]);
                        setOrganizations([]);
                        setSystemRoles([]);
                        setPublicRead(false);
                    } }, t('common.clearAll')),
                React.createElement(Button, { sx: { mr: 2, mb: 2 }, variant: 'outlined', color: 'primary', onClick: handleRemoveSelectedOrganizationsAndAccounts, "data-test": 'remove-permissions-button' }, t('common.remove'))),
            React.createElement(PermissionsAccordions, { organizations: organizations, accounts: accounts, systemRoles: systemRoles, onPermissionChange: handleSinglePermissionChange, selectedPermissionsMembers: {
                    accountIds: selectedAccountsIds,
                    organizationIds: selectedOrganizationsIds,
                    systemRoles: selectedSystemRoles,
                }, onSelectedPermissionsMembers: handleSelectedAccountsAndOrganizationsIds, canAccessAccounts: canAccessAccounts })),
        React.createElement(DialogActions, null,
            React.createElement(Button, { color: 'primary', onClick: () => props.onCancel(), "data-test": 'cancel-button' }, t('common.cancel')),
            React.createElement(Button, { color: 'primary', onClick: handleSave, "data-test": 'save-button' }, t('common.save'))),
        React.createElement(Dialog, { fullScreen: true, open: addAccountsModalOpen, onClose: () => setAddAccountsModalOpen(false) },
            React.createElement(PermissionsSelection, { canAccessAccounts: canAccessAccounts, ownerId: props.ownerId, handleSave: handleSelectionSave, handleClose: () => setAddAccountsModalOpen(false) }))));
};
